import { type ReactNode } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Sparkline } from './Sparkline';
import { DeltaBadge } from './DeltaBadge';
import { MotionCard } from '@/components/motion/MotionCard';
import { AnimatedNumber } from '@/components/motion/AnimatedNumber';
import type { Metric } from '@/lib/periods';
import { fmtISTRange } from '@/lib/format/date';
import { cn } from '@/lib/utils';

interface Props {
  label: string;
  metric: Metric | null | undefined;
  format: (n: number) => string;
  sparkline?: number[];
  comparisonRange?: { start: Date | string; end: Date | string };
  comparisonLabel?: string;
  /** Negative delta = positive outcome (e.g. churn, response time). */
  invertDelta?: boolean;
  loading?: boolean;
  delay?: number;
  /** Optional icon or accent. */
  icon?: ReactNode;
  className?: string;
}

export function KpiTile({
  label,
  metric,
  format,
  sparkline,
  comparisonRange,
  comparisonLabel,
  invertDelta,
  loading,
  delay,
  icon,
  className,
}: Props) {
  if (loading || !metric) {
    return (
      <Card className={cn('overflow-hidden', className)}>
        <CardHeader>
          <CardTitle>{label}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-9 w-32" />
          <Skeleton className="mt-3 h-3 w-20" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-7 w-24" />
        </CardFooter>
      </Card>
    );
  }

  const tone =
    metric.deltaPct === 0
      ? 'neutral'
      : invertDelta
        ? metric.deltaPct < 0
          ? 'positive'
          : 'negative'
        : metric.deltaPct > 0
          ? 'positive'
          : 'negative';

  const comparison =
    comparisonRange &&
    `${comparisonLabel ?? 'vs'} ${fmtISTRange(comparisonRange.start, comparisonRange.end)}`;

  return (
    <MotionCard delay={delay}>
      <Card className={cn('group overflow-hidden', className)}>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <CardTitle className="leading-snug">{label}</CardTitle>
          {icon && <div className="text-muted-foreground/70">{icon}</div>}
        </CardHeader>
        <CardContent className="flex items-end justify-between gap-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-default font-semibold tracking-tight tabular-nums text-3xl">
                <AnimatedNumber value={metric.current} format={format} />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1 font-mono text-xs">
                <div>Current: {format(metric.current)}</div>
                <div>Previous: {format(metric.previous)}</div>
                <div>Δ: {format(metric.delta)}</div>
              </div>
            </TooltipContent>
          </Tooltip>
          {sparkline && sparkline.length > 0 && (
            <Sparkline data={sparkline} tone={tone} ariaLabel={`${label} trend`} />
          )}
        </CardContent>
        <CardFooter className="flex items-center justify-between gap-2">
          <DeltaBadge deltaPct={metric.deltaPct} invert={invertDelta} />
          {comparison && <span className="truncate font-mono text-2xs">{comparison}</span>}
        </CardFooter>
      </Card>
    </MotionCard>
  );
}
